import { useRef, useState, useLayoutEffect, useEffect } from 'react';
import curry from 'ramda/src/curry';
import { useRouter } from 'next/router';

import { success } from '/components/ui/toast';
import { productPageApi, ProductPage } from '/api/page';
import { nanoid } from 'nanoid';

export const emptyPage: ProductPage = {
    uuid: '',
    type: 'product',
    id: 0,
    parentId: null,
    published: false,
    title: '',
    slug: '',
    path: '',
    data: {
        content: '',
        description: '',
        eiProduct: null,
        images: [],
        specification: [],
    },
    createdAt: '',
    updatedAt: '',
};

export default function useProductPage(id?: number | 'new') {
    const router = useRouter();

    const [page, setPage] = useState<ProductPage>(emptyPage);
    const [notFound, setNotFound] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        if (!id) return;

        if (id !== 'new') {
            (async () => {
                const pageRes = await productPageApi.getById(id);

                if ('error' in pageRes) return setNotFound(true);

                setPage({
                    ...pageRes,
                    data: {
                        ...pageRes.data,
                        specification: (pageRes.data.specification || []).map(
                            (spec) => ({
                                ...spec,
                                id: spec.id || nanoid(9),
                            })
                        ),
                    },
                });
            })();
        }
    }, [id]);

    const callbackRef = useRef<(a: ProductPage, b: ProductPage) => void>(
        () => {}
    );

    const prevStateRef = useRef<ProductPage>(page);

    useLayoutEffect(
        () => callbackRef.current(prevStateRef.current, page),
        [page]
    );

    const setPageProp = curry((key, value, callback = () => {}) => {
        prevStateRef.current = page;
        callbackRef.current = callback;
        setPage({ ...page, [key]: value });
    });

    const setPageDataProp = curry((key, value, callback = () => {}) => {
        prevStateRef.current = page;
        callbackRef.current = callback;
        setPage({ ...page, data: { ...page.data, [key]: value } });
    });

    const createPage = async () => {
        setError('');
        const resp = await productPageApi.create(page);
        if ('error' in resp) return setError(resp.error);
        success('Page created');
        router.push(`/produkt/${resp.id}`);
        setPage(resp);
    };

    const patchPage = async () => {
        setError('');
        const resp = await productPageApi.update(page.id, page);
        if ('error' in resp) return setError(resp.error);
        success('Page updated');
    };

    const deletePage = async () => {
        setError('');
        const resp = await productPageApi.delete(page.id);
        if ('error' in resp) return setError(resp.error);
        router.push(`/produkter/liste`);
    };

    const duplicatePage = async () => {
        setError('');
        const resp = await productPageApi.create({
            ...page,
            id: 0,
            slug: '',
            title: page.title + ' (duplicate)',
        });
        if ('error' in resp) return setError(resp.error);
        success('Page duplicated');
        router.push(`/produkt/${resp.id}`);
        setPage(resp);
    };

    return {
        notFound,
        page,
        setPage,
        setPageProp,
        setPageDataProp,
        createPage,
        duplicatePage,
        patchPage,
        deletePage,
        error,
    };
}
